/**
 * Projects API
 *
 * Handles all project-related API calls.
 * Uses Zod validation for critical endpoints.
 */

import { BaseApiClient, type ExpectedRefusal } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import {
  listProjectsResponseSchema,
  getProjectResponseSchema,
  listProjectSupervisorsResponseSchema,
  qualityWeightsResponseSchema,
  qualityWeightsPreviewResponseSchema,
} from '@aica/api-types/schemas';
import type {
  ProjectResponse,
  ListProjectSupervisorsResponse,
  UpdateProjectSupervisorsResponse,
} from '@aica/api-types/schemas';
import type { UpdateProjectRequest } from '@aica/api-types/schemas';
import type {
  AlertNotificationChannels,
  TestAlertNotificationChannelResponse,
  QualityWeights,
  QualityWeightsResponse,
  QualityWeightsPreviewResponse,
} from '@aica/api-types/schemas';
import type {
  ProjectWithStats,
  ProjectsListResponse,
  ProjectStatsResponse,
  SuccessResponse,
} from '../types';

/** 409 of the weights PUT: someone saved the project's weights after this editor loaded them. */
export const QUALITY_WEIGHTS_CONFLICT_CODE = 'QUALITY_WEIGHTS_CONFLICT';

// A concurrent save is an outcome the section handles, not a fault to report.
const QUALITY_WEIGHTS_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 409, code: QUALITY_WEIGHTS_CONFLICT_CODE },
];

export class ProjectsApi extends BaseApiClient {
  /**
   * Get projects list - validated with Zod
   */
  async getProjects(params?: Record<string, string>): Promise<ProjectsListResponse> {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const validated = await this.validatedRequest(
      `${API_ENDPOINTS.PROJECTS}${query}`,
      listProjectsResponseSchema
    );
    return validated as unknown as ProjectsListResponse;
  }

  /**
   * Get single project - validated with Zod
   */
  async getProjectById(id: string): Promise<ProjectWithStats> {
    const validated = await this.validatedRequest<{ project: ProjectResponse }>(
      API_ENDPOINTS.PROJECTS_BY_ID(id),
      getProjectResponseSchema
    );
    return validated.project as unknown as ProjectWithStats;
  }

  async getProjectStats(id: string): Promise<ProjectStatsResponse> {
    return this.request<ProjectStatsResponse>(API_ENDPOINTS.PROJECTS_STATS(id));
  }

  /**
   * Supervisors of the organization, each flagged with whether they oversee
   * this project. The whole roster, not just the assigned ones — the screen is
   * a checklist.
   *
   * Validated against the shared schema rather than a hand-written type: the
   * response shape lived in two places that could drift silently, and a renamed
   * field would have surfaced only as an empty checklist.
   */
  async getProjectSupervisors(
    projectId: string
  ): Promise<ListProjectSupervisorsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROJECTS_SUPERVISORS(projectId),
      listProjectSupervisorsResponseSchema
    );
  }

  /**
   * Replace the supervisors assigned to this project. Rows belonging to other
   * roles — the creator's project-admin row above all — are left untouched.
   */
  async updateProjectSupervisors(
    projectId: string,
    userIds: string[]
  ): Promise<UpdateProjectSupervisorsResponse> {
    return this.request(API_ENDPOINTS.PROJECTS_SUPERVISORS(projectId), {
      method: 'PUT',
      body: JSON.stringify({ userIds }),
    });
  }

  async createProject(data: Partial<ProjectWithStats>): Promise<ProjectWithStats> {
    return this.request<ProjectWithStats>(API_ENDPOINTS.PROJECTS, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateProject(id: string, data: UpdateProjectRequest): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.PROJECTS_BY_ID(id), {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteProject(id: string): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.PROJECTS_BY_ID(id), {
      method: 'DELETE',
    });
  }

  async archiveProject(
    id: string
  ): Promise<{ success: boolean; status: string; updatedAt: string }> {
    return this.request<{ success: boolean; status: string; updatedAt: string }>(
      API_ENDPOINTS.PROJECTS_ARCHIVE(id),
      {
        method: 'PATCH',
      }
    );
  }

  async updateProjectSettings(
    id: string,
    settings: Record<string, unknown>
  ): Promise<SuccessResponse> {
    return this.request<SuccessResponse>(API_ENDPOINTS.PROJECTS_SETTINGS(id), {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async getChannelSettings(projectId: string): Promise<Record<string, unknown>> {
    return this.request(API_ENDPOINTS.PROJECTS_CHANNEL_SETTINGS(projectId));
  }

  async updateChannelSettings(
    projectId: string,
    settings: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    return this.request(API_ENDPOINTS.PROJECTS_CHANNEL_SETTINGS(projectId), {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  /**
   * Team alert-delivery channels — raw passthrough, deliberately NOT run
   * through validatedRequest(): that helper throws on a schema mismatch,
   * which would blank the whole settings section instead of degrading
   * gracefully. useAlertChannels() safeParses the `channels` field itself
   * and exposes `isError` without throwing.
   */
  async getAlertNotificationChannels(projectId: string): Promise<{ channels: unknown }> {
    return this.request(API_ENDPOINTS.PROJECTS_ALERT_NOTIFICATION_CHANNELS(projectId));
  }

  /**
   * Full replace of alertNotificationChannels — the caller must always send
   * all six keys (see the plan's Global Constraints); this method does not
   * merge with the existing config.
   */
  async updateAlertNotificationChannels(
    projectId: string,
    data: AlertNotificationChannels
  ): Promise<{ success: boolean; channels: unknown }> {
    return this.request(API_ENDPOINTS.PROJECTS_ALERT_NOTIFICATION_CHANNELS(projectId), {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** The weights the project's score reads; `null` — equal (#874). */
  async getQualityWeights(projectId: string): Promise<QualityWeightsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROJECTS_QUALITY_WEIGHTS(projectId),
      qualityWeightsResponseSchema
    );
  }

  /**
   * Save `weights` (`null` resets to equal); answers what is now stored.
   * `expectedWeights` — what the editor loaded: a project saved by someone
   * else since answers 409 `QUALITY_WEIGHTS_CONFLICT`.
   */
  async updateQualityWeights(
    projectId: string,
    weights: QualityWeights | null,
    expectedWeights: QualityWeights | null
  ): Promise<QualityWeightsResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROJECTS_QUALITY_WEIGHTS(projectId),
      qualityWeightsResponseSchema,
      {
        method: 'PUT',
        body: JSON.stringify({ weights, expectedWeights }),
        expectedRefusals: QUALITY_WEIGHTS_REFUSALS,
      }
    );
  }

  /** What saving `weights` would change on the project's recent calls. */
  async previewQualityWeights(
    projectId: string,
    weights: QualityWeights | null
  ): Promise<QualityWeightsPreviewResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.PROJECTS_QUALITY_WEIGHTS_PREVIEW(projectId),
      qualityWeightsPreviewResponseSchema,
      { method: 'POST', body: JSON.stringify({ weights }) }
    );
  }

  async testAlertNotificationChannel(
    projectId: string,
    channel: 'email' | 'telegram' | 'webhook' | 'bitrix24_chat'
  ): Promise<TestAlertNotificationChannelResponse> {
    return this.request(
      API_ENDPOINTS.PROJECTS_ALERT_NOTIFICATION_CHANNELS_TEST(projectId),
      {
        method: 'POST',
        body: JSON.stringify({ channel }),
      }
    );
  }
}
